import {isPlainObject} from 'is-plain-object';
import CryptoJS from 'crypto-js';
import Bowser from 'bowser';
import Video from 'twilio-video';

export const isMobile = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

// Recursively removes any object keys with a value of undefined
export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj;

  const target: { [name: string]: any } = {};

  for (const key in obj) {
    const val = obj[key];
    if (typeof val !== 'undefined') {
      target[key] = removeUndefineds(val);
    }
  }

  return target as T;
}

export async function getDeviceInfo() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
    videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
    audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.some(device => device.kind === 'audioinput'),
    hasVideoInputDevices: devices.some(device => device.kind === 'videoinput'),
  };
}

// This function will return 'true' when the specified permission has been denied by the user.
// If the API doesn't exist, or the query function returns an error, 'false' will be returned.
export async function isPermissionDenied(name: 'camera' | 'microphone') {
  const permissionName = name as PermissionName; // workaround for https://github.com/microsoft/TypeScript/issues/33923

  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name: permissionName });
      return result.state === 'denied';
    } catch {
      return false;
    }
  } else {
    return false;
  }
}

export function getDecryptedPWD(pwd: string) {
  const { REACT_APP_MEETING_ENCODE_KEY } = process.env;

  const DEFAULT_KEY = "3@aeTpak2!1";
  const DEFAULT_KEY2 = "!kT2a@p31";
  const MEETING_ENCODE_KEY_PREVIOUS = "1!2kapTea@3"
  // Create an array of keys to try: Current -> Previous (if exists) -> Default
  const keysToTry = [REACT_APP_MEETING_ENCODE_KEY];
  
  // Always include the default key as a fallback, but avoid duplicates if it matches one of the env vars
  if (!keysToTry.includes(DEFAULT_KEY)) {
    keysToTry.push(DEFAULT_KEY);
  }

  // Always include the default key as a fallback, but avoid duplicates if it matches one of the env vars
  if (!keysToTry.includes(DEFAULT_KEY2)) {
    keysToTry.push(DEFAULT_KEY2);
  }

  if (MEETING_ENCODE_KEY_PREVIOUS) {
    keysToTry.push(MEETING_ENCODE_KEY_PREVIOUS);
  }

  // Filter out any undefined/null keys just in case
  const validKeys = keysToTry.filter(Boolean);

  let decode = '';

  if (pwd) {
    for (const key of validKeys) {
      try {
        const bytes = CryptoJS.AES.decrypt(pwd, `${key}`);
        const decryptedStr = bytes.toString(CryptoJS.enc.Utf8);

        // Verify if it looks like valid JSON (our tokens are JSON strings)
        // or just check if we got a non-empty string back which usually means success with AES defaults in this context
        if (decryptedStr) {
           // Optional: Try to parse it to be 100% sure it's valid data
           try {
             JSON.parse(decryptedStr);
             decode = decryptedStr;
             console.log("Working key", key, decode)
             break; // Found a working key
           } catch (e) {
             // If it's not valid JSON, it might be a wrong key decryption result (garbage data)
             // Continue to next key
           }
        }
      } catch (err) {
        // Decryption failed, try next key
        continue;
      }
    }
  }
  return decode;
}

export function maskIdentity(identity: string, meeting: any) {
  const datetime = new Date().getTime();
  let identifier = '###';

  if (meeting && Boolean(meeting.isChaperoning) === true) {
    identifier = '###CP##';
  } else {
    if (meeting && meeting && meeting.is_anonymous) {
      identifier = '###AP##';
    }
  }

  const maskedIdentity = `${identity}${identifier}${datetime}`;

  return maskedIdentity;
}

export function getIdentityInitials(identity: string) {
  const identifier = identity.split('###')[1] || '';
  let title = identity.split('###')[0];

  if (identifier.startsWith('AP##')) {
    title = 'Anonymous Participant';
  } else if (identifier.startsWith('CP##')) {
    title = 'Chaperoning Mode';
  }

  return title
    .split(' ')
    .map((x: string) => {
      return x[0];
    })
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function removeIdentityMasking(identity: string) {
  const identifier = identity.split('###')[1] || '';
  let title = identity.split('###')[0];

  if (identifier.startsWith('AP##')) {
    title = 'Anonymous Participant';
  } else if (identifier.startsWith('CP##')) {
    title = 'Chaperoning Mode';
  }

  return title;
}

export function getBrowserDetails() {
  const browserDetails = Bowser.parse(window.navigator.userAgent);

  return browserDetails;
}

export const getQualityScore = (
  stat: number,
  goodThreshold: number,
  suboptimalThreshold: number,
  poorThreshold: number,
  descending?: boolean
) => {
  console.log(stat, goodThreshold, suboptimalThreshold, poorThreshold, 'NUMBERS');
  if (descending) {
    if (stat > goodThreshold) return 'excellent';
    if (stat > suboptimalThreshold) return 'good';
    if (stat > poorThreshold) return 'suboptimal';
    return 'poor';
  }
  if (stat >= poorThreshold) return 'poor';
  if (stat >= suboptimalThreshold) return 'suboptimal';
  if (stat >= goodThreshold) return 'good';
  return 'excellent';
};

export const troubleShoot = async () => {
  const audioPermission = await navigator.mediaDevices
    .enumerateDevices()
    .then(devices => devices.filter(d => d.kind === 'audioinput').every(d => d.label));
  const videoPermission = await navigator.mediaDevices
    .enumerateDevices()
    .then(devices => devices.filter(d => d.kind === 'videoinput').every(d => d.label));
  const isSupported = Video?.isSupported ? true : false;

  return { audioPermission: audioPermission, videoPermission: videoPermission, browserSupported: isSupported };
};
